/**
 * WebSocket Provider Component
 * Initializes and manages global WebSocket connection for authenticated users
 */

import React, { useEffect, useState, useCallback } from "react";
import { useSelector, shallowEqual } from "react-redux";
import globalWebSocketService from "../services/globalWebSocketService";
import { ToastNotification } from "../components/ui";
import IncomingCallPopup from "./IncomingCallPopup";

const WebSocketProvider = ({ children }) => {
  const { authToken } = useSelector(
    ({ auth }) => ({
      authToken: auth.authToken,
    }),
    shallowEqual
  );

  const [toastConfig, setToastConfig] = useState({
    open: false,
    title: "",
    message: "",
    tone: "info",
    duration: 6000,
  });

  const [callPopup, setCallPopup] = useState({
    open: false,
    callerNumber: "",
    callerName: null,
    contact: null,
    account: null,
    recentTickets: [],
    uniqueid: "",
  });

  const isAuthorized = Boolean(authToken);

  useEffect(() => {
    if (!isAuthorized || !authToken) {
      globalWebSocketService.disconnect();
      return undefined;
    }

    const sanitizedToken =
      typeof authToken === "string"
        ? authToken.replace(/['"]+/g, "")
        : authToken;

    let storedDetails = {};
    try {
      storedDetails = JSON.parse(localStorage.getItem("userDetails")) || {};
    } catch (error) {
      console.warn("Unable to parse stored user details:", error);
    }

    const userId =
      storedDetails?.pk || storedDetails?.user_id || storedDetails?.id;
    const accountId =
      storedDetails?.account_id ||
      storedDetails?.account?.pk ||
      storedDetails?.account?.id;

    if (!sanitizedToken || !userId) {
      console.warn(
        "Missing authentication details for WebSocket connection",
        { hasToken: Boolean(sanitizedToken), userId }
      );
      return undefined;
    }

    globalWebSocketService.connect({
      token: sanitizedToken,
      user_id: userId,
      account_id: accountId,
    });

    return () => {
      globalWebSocketService.disconnect();
    };
  }, [isAuthorized, authToken]);

  const normalizeTicketEvent = useCallback((payload) => {
    if (!payload) return null;

    const envelope = payload?.content?.data?.data || payload?.content?.data || payload?.content || payload?.data || payload;
    if (!envelope || typeof envelope !== "object") {
      return null;
    }

    // Only surface toasts for brand-new inbound tickets
    const isInbound = envelope.direction === "inbound";
    const updateType = envelope.update_type || envelope.status || envelope.status_current;
    const isNewStatus = (envelope.status || envelope.status_current) === "new";
    const isNewTicketEvent = envelope.event_type === "email" && (!updateType || updateType === "general");

    if (!isInbound || !isNewStatus || !isNewTicketEvent) {
      return null;
    }

    const stripHtml = (str = "") =>
      typeof str === "string"
        ? str.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()
        : "";

    const ticketId = envelope.ticket_id || envelope.ticketId;
    const subject = envelope.ticket_title || envelope.subject || envelope.title;
    const preview =
      stripHtml(envelope.content) ||
      stripHtml(envelope.message_preview) ||
      "New ticket received";

    const title = subject
      ? ticketId
        ? `New Ticket #${ticketId} - ${subject}`
        : subject
      : ticketId
      ? `New Ticket #${ticketId}`
      : "New Ticket";

    return { title, preview };
  }, []);

  useEffect(() => {
    const unsubscribe = globalWebSocketService.addEventListener("ticket_event", (payload) => {
      const normalized = normalizeTicketEvent(payload);
      if (!normalized) return;

      setToastConfig({
        open: true,
        title: normalized.title,
        message: normalized.preview,
        tone: "info",
        duration: 7000,
      });
    });

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [normalizeTicketEvent]);

  useEffect(() => {
    const unsubscribe = globalWebSocketService.addEventListener(
      "incoming_call",
      (payload) => {
        // Unwrap nested envelope: payload may be { content: { data: { ...fields } } }
        const callData = payload?.content?.data || payload;
        setCallPopup({
          open: true,
          callerNumber: callData?.caller_number || "Unknown",
          callerName: callData?.caller_name || null,
          contact: callData?.contact || null,
          account: callData?.account || null,
          recentTickets: callData?.recent_tickets || [],
          uniqueid: callData?.uniqueid || "",
        });
      }
    );
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  return (
    <>
      {children}
      <ToastNotification
        isOpen={toastConfig.open}
        title={toastConfig.title}
        message={toastConfig.message}
        tone={toastConfig.tone}
        autoHideDuration={toastConfig.duration}
        onClose={() => setToastConfig((prev) => ({ ...prev, open: false }))}
      />
      <IncomingCallPopup
        isOpen={callPopup.open}
        callerNumber={callPopup.callerNumber}
        callerName={callPopup.callerName}
        contact={callPopup.contact}
        account={callPopup.account}
        recentTickets={callPopup.recentTickets}
        uniqueid={callPopup.uniqueid}
        onClose={() => setCallPopup((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
};

export default WebSocketProvider;
